import { SOCKET_STREAMING_URL, STREAMING_EMITTER_CONSTANTS } from '@/shared/config';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { generateToken } from '..';
import emitter from '../eventEmitter';

const ActionEnum = {
  JOIN_ROOM: 'joinRoom',
  SEND_MESSAGE: 'sendMessage',
  MESSAGE: 'message',
  ROOM_CHANGED: 'roomChanged',
};

/**
 * Announces that the ECG streaming socket has opened.
 * Emits WEBSOCKET_CONNECTED on the shared event emitter.
 *
 * @returns Nothing.
 */
const onWebsocketConnected = () => {
  emitter.emit(STREAMING_EMITTER_CONSTANTS.WEBSOCKET_CONNECTED);
};

/**
 * Dispatches one ECG streaming frame to the listeners interested in its action.
 * Parses the frame's JSON payload and re-emits roomChanged frames as ON_ROOM_CHANGED and message
 * frames as ON_MESSAGE_DATA, ignoring every other action.
 *
 * @param data - The socket message event whose data holds the JSON frame.
 * @returns Nothing.
 */
const onWebsocketMessage = (data: any) => {
  const parsedData = JSON.parse(data.data);
  switch (parsedData.action) {
    case ActionEnum.ROOM_CHANGED:
      emitter.emit(STREAMING_EMITTER_CONSTANTS.ON_ROOM_CHANGED, parsedData);
      break;
    case ActionEnum.MESSAGE:
      emitter.emit(STREAMING_EMITTER_CONSTANTS.ON_MESSAGE_DATA, parsedData);
      break;
    default:
      break;
  }
};

/**
 * Reports that the ECG streaming socket has closed.
 * Logs the close event to the console.
 *
 * @param event - The socket close event.
 * @returns Nothing.
 */
const onWebsocketClose = (event: any) => {
  console.log('onWebsocketClose ', event);
};

/**
 * Reports an error raised by the ECG streaming socket.
 * Logs the error event to the console.
 *
 * @param event - The socket error event.
 * @returns Nothing.
 */
const onWebsocketError = (event: any) => {
  console.log('onWebsocketError ', event);
};

/**
 * WebSocket client for the live ECG streaming service, managing the connection, room membership and message dispatch. Opens a ReconnectingWebSocket with a generated auth token and arraybuffer frames, sends JOIN_ROOM and SEND_MESSAGE actions, and re-emits ROOM_CHANGED and MESSAGE frames onto the streaming emitter while logging close and error events.
 */
class SocketClient {
  socket: any;

  isConnected: any;

  constructor() {
    this.socket = null;
    this.isConnected = false;
  }

  connectToServer = async () => {
    if (!this.socket) {
      const accessToken = await generateToken();
      this.socket = new ReconnectingWebSocket(`${SOCKET_STREAMING_URL}?Auth=${accessToken}`);
      this.socket.binaryType = 'arraybuffer';
      this.socket.addEventListener('open', onWebsocketConnected);
      this.socket.addEventListener('message', onWebsocketMessage);
      this.socket.addEventListener('close', onWebsocketClose);
      this.socket.addEventListener('error', onWebsocketError);
    }
  };

  joinRoom = (roomId: any) => {
    this.socket.send(
      JSON.stringify({
        action: ActionEnum.JOIN_ROOM,
        roomId,
      }),
    );
  };

  sendMessage = ({ token, connectionIds, payload }: any) => {
    this.socket.send(
      JSON.stringify({
        action: ActionEnum.SEND_MESSAGE,
        token,
        connectionIds,
        payload,
      }),
    );
  };

  close = () => {
    if (this.socket) {
      this.socket.close(1000, 'close');
      this.socket = undefined;
    }
  };
}

const staticSocket = new SocketClient();

export default staticSocket;
